import { ReactNode } from 'react';
import styles from './EmailSubscription.module.scss';
import CtaButton from 'components/helpers/Atoms/Cta/CtaButton';
import { MessageBanner } from 'components/helpers/Atoms/FormElements/MessageBanner';
import { useEmailSubscription } from 'lib/hooks/useEmailSubscription';
import { sendNewsletterOptInAnalytics } from 'components/helpers/Analytics/NewsletterSignupAnalytics';
import { useGlobalData } from 'lib/hooks/sitecore/useGlobalData';
import { useSiteSettings } from 'lib/hooks/useSiteSettings';

export interface EmailSubscriptionProps {
  title: ReactNode;
  sectionDescription: ReactNode;
  description: ReactNode;
  errorMessage: ReactNode;
  errorSecondaryMessage: ReactNode;
  subscribeButtonText: ReactNode;
  subscribeButtonAriaLabel?: string;
  successMessage: ReactNode;
  unsubscribeButtonText: ReactNode;
  unsubscribeButtonAriaLabel?: string;
  newsLetterSubscriptionId?: string;
  isSubscribedEmailSubscription: boolean;
}

const EmailSubscription = (props: EmailSubscriptionProps) => {
  const {
    title,
    sectionDescription,
    description,
    errorMessage,
    errorSecondaryMessage,
    subscribeButtonText,
    subscribeButtonAriaLabel,
    successMessage,
    unsubscribeButtonText,
    unsubscribeButtonAriaLabel,
    newsLetterSubscriptionId,
    isSubscribedEmailSubscription,
  } = props;

  const { siteName } = useGlobalData();
  const siteSettings = useSiteSettings();
  const newsletterTitle = siteSettings?.newsletterTitle?.value || `${siteName}'s Email`;

  const { isLoading, error, success, handleToggleSubscription } =
    useEmailSubscription(newsLetterSubscriptionId);

  const handleSubscriptionClick = async () => {
    const isSubscribing = !isSubscribedEmailSubscription;
    const { success: toggleSuccess } = await handleToggleSubscription(
      isSubscribedEmailSubscription
    );

    if (isSubscribing && toggleSuccess && newsLetterSubscriptionId) {
      sendNewsletterOptInAnalytics({
        newsletterTitles: [newsletterTitle],
        newsletterIds: [newsLetterSubscriptionId],
        subscriberPosition: 'MyAccount',
      });
    }
  };

  return (
    <div className={styles.emailSubscriptionContainer}>
      <div className={styles.emailSubscriptionSectionContainer}>
        <div className={styles.emailSubscriptionSectionWrapper}>
          <h2 className={styles.emailSubscriptionTitle}>{title}</h2>
        </div>
        <hr className={styles.divider} />
      </div>

      <div className={styles.emailSubscriptionWrapper}>
        {error && (
          <MessageBanner
            type="error"
            message={`${errorMessage} | ${errorSecondaryMessage}`}
            autoFocus
          />
        )}

        {success && <MessageBanner type="success" message={successMessage} autoFocus />}
        {description && <div className={styles.emailSubscriptionDescription}>{description}</div>}
        {sectionDescription && !isSubscribedEmailSubscription && (
          <div className={styles.emailSubscriptionSectionDescription}>{sectionDescription}</div>
        )}
        <CtaButton
          ctaStyle="filled"
          ctaVariant="brand"
          type="button"
          disabled={isLoading}
          aria-label={
            isSubscribedEmailSubscription ? unsubscribeButtonAriaLabel : subscribeButtonAriaLabel
          }
          onClick={handleSubscriptionClick}
        >
          {isSubscribedEmailSubscription ? unsubscribeButtonText : subscribeButtonText}
        </CtaButton>
      </div>
    </div>
  );
};

export default EmailSubscription;
